/**
 * FilterSortSearch — filter / sort / search controls for the Hub Feed.
 *
 * Exposes two composable pieces so the parent can lay them out freely:
 * - {@link SortBar}   — Trending | Recent | Top sort toggle
 * - {@link SearchBox} — q= free-text search input
 *
 * HubFeed places {@link SortBar} on the left of the merged control row
 * (alongside the view toggle + New Post action cluster) and drops
 * {@link SearchBox} full-width on its own row beneath it.
 *
 * Design source: design/project/scripts/app.jsx controls bar (lines 177-189).
 * All filtering, sorting, and searching execute server-side — these components
 * only manage the UI state and report changes via callbacks.
 *
 * Usage:
 *   <SortBar filters={filters} onFiltersChange={setFilters} />
 *   <SearchBox filters={filters} onFiltersChange={setFilters} />
 *
 * where `filters` is a {@link FeedFilters} object and `onFiltersChange` is
 * called whenever any control changes.  The parent (HubFeed) passes the
 * updated filters to `useFeed` which calls the API with the new params.
 */

import { useCallback, useRef } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Valid sort values (mirrors feed API sort param). */
export type SortOption = "trending" | "recent" | "top";

/**
 * All filter/sort/search state that maps 1:1 to the feed API query params.
 *
 * Upstream: passed directly to fetchFeed() as FeedQueryParams.
 */
export interface FeedFilters {
  /** Sort order. Default "trending" matches the API default. */
  sort: SortOption;
  /** Free-text search query.  Empty string → no ?q= param sent. */
  query: string;
}

/** Default (no-op) filter state — matches API defaults. */
export const DEFAULT_FEED_FILTERS: FeedFilters = {
  sort: "trending",
  query: "",
};

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const SORT_OPTIONS: { id: SortOption; label: string; title: string }[] = [
  {
    id: "trending",
    label: "🔥 Trending",
    title: "Score = votes + recency bonus (8 for posts within 7 days)",
  },
  { id: "recent", label: "Recent", title: "Newest first" },
  { id: "top", label: "Top", title: "Most upvoted" },
];

// ---------------------------------------------------------------------------
// Shared props
// ---------------------------------------------------------------------------

interface ControlProps {
  filters: FeedFilters;
  onFiltersChange: (filters: FeedFilters) => void;
  /** Disable controls while the feed is loading (prevents double-fetches). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// SortBar
// ---------------------------------------------------------------------------

/**
 * SortBar renders the Trending | Recent | Top sort toggle.  Stateless —
 * receives the current filter state and reports changes via `onFiltersChange`.
 */
export function SortBar({ filters, onFiltersChange, disabled = false }: ControlProps) {
  const setSort = useCallback(
    (sort: SortOption) => {
      onFiltersChange({ ...filters, sort });
    },
    [filters, onFiltersChange],
  );

  return (
    <div
      className="hub-sortbar"
      role="group"
      aria-label="Sort feed"
      data-testid="filter-sort-search"
    >
      {SORT_OPTIONS.map(({ id, label, title }) => {
        const active = filters.sort === id;
        return (
          <button
            key={id}
            type="button"
            className={`hub-sortbtn${active ? " hub-sortbtn--active" : ""}`}
            aria-pressed={active}
            title={title}
            disabled={disabled}
            onClick={() => setSort(id)}
            data-testid={`sort-${id}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SearchBox
// ---------------------------------------------------------------------------

/**
 * SearchBox renders the free-text search input.  Stateless — reports changes
 * via `onFiltersChange`, debounced to avoid a fetch on every keystroke.
 *
 * Server-side contract: this component never filters the displayed list
 * itself.  Every change fires `onFiltersChange`, which the caller uses to
 * re-fetch from the API with the updated query parameters.
 */
export function SearchBox({ filters, onFiltersChange, disabled = false }: ControlProps) {
  // Debounce search input to avoid a fetch on every keystroke.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onFiltersChange({ ...filters, query: value });
      }, 300); // [STUB] 300ms debounce; production may tune this
    },
    [filters, onFiltersChange],
  );

  const clearSearch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onFiltersChange({ ...filters, query: "" });
  }, [filters, onFiltersChange]);

  return (
    <div className="hub-feed__search">
      <div className="hub-searchbox" role="search">
        {/* Search icon */}
        <svg
          className="hub-searchbox__icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          className="hub-searchbox__input"
          type="search"
          placeholder="Search title, excerpt, tags, author…"
          defaultValue={filters.query}
          onChange={handleSearchChange}
          disabled={disabled}
          aria-label="Search the hub"
          data-testid="search-input"
        />
        {filters.query && (
          <button
            type="button"
            className="hub-searchbox__clear"
            aria-label="Clear search"
            onClick={clearSearch}
            data-testid="search-clear"
          >
            {/* × icon */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSS classes used (defined in hub-feed.css):
//
//   .hub-sortbar                — sort toggle button group
//   .hub-sortbtn                — individual sort button
//   .hub-sortbtn--active        — active sort button
//   .hub-feed__search           — full-width search row wrapper
//   .hub-searchbox              — search field wrapper (icon + input + clear)
//   .hub-searchbox__icon        — magnifying-glass svg
//   .hub-searchbox__input       — <input type="search">
//   .hub-searchbox__clear       — × clear button (shown when query is non-empty)
// ---------------------------------------------------------------------------
